/**
 * Multi-year capacity projection, ported from loader-budget.js (logic only;
 * docs/appendix-A-loader-port-manifest.md, section 11). Per fiscal quarter the
 * missions are grown, a 7-day sample of contact windows is allocated greedily
 * and SLA, conflicts and utilisation are recorded. The async window source is
 * always awaited (the original emitted stub rows in worker mode) and the
 * saturation antenna is the one at maximum utilisation, see PORT_NOTES.md.
 */
import type { Antenna, ContactWindowMap, Mission, ProjectedGrowth } from '@/features/loader/types';
import type { BudgetProjection, BudgetRow, GrowthModel, SaturationPoint } from './types';
import { computeAllContactWindows, type ComputeContactWindows, type ProgressCallback } from './conflicts';

const DAY_MS = 86_400_000;
const HOUR_MS = 3_600_000;
const MINUTE_MS = 60_000;
const SAMPLE_DAYS = 7;

export interface ProjectionInput {
  missions: Mission[];
  antennas: Antenna[];
  startFY: number;
  endFY: number;
  growthModel: GrowthModel;
  isToggleEnabled: (id: string) => boolean;
  computeContactWindows: ComputeContactWindows;
  /** Draw behind synthetic windows of TLE-less missions. */
  random?: () => number;
  nowMs?: () => number;
  /** Called before each quarter is projected and once at 100% when done. */
  onProgress?: ProgressCallback;
}

export interface Quarter {
  fy: number;
  quarter: number;
  startMs: number;
  endMs: number;
}

export interface QuickAnalysisResult {
  avgSLA: number;
  conflicts: number;
  utilization: Record<string, number>;
}

/** Annual growth rate per model: aggressive 10%, standard 5%, flat 0. */
export function growthRateFor(model: GrowthModel): number {
  return model === 'aggressive' ? 0.1 : model === 'flat' ? 0 : 0.05;
}

/** Run the projection over startFY..endFY (four quarters each). */
export async function runProjection(input: ProjectionInput): Promise<BudgetProjection> {
  const { missions, antennas, startFY, endFY, growthModel, isToggleEnabled } = input;
  const random = input.random ?? Math.random;
  const nowMs = input.nowMs ?? Date.now;
  const growthRate = growthRateFor(growthModel);

  const yearlyResults: BudgetRow[] = [];
  const quarters = buildQuarterlyTimeline(startFY, endFY);
  for (const [index, q] of quarters.entries()) {
    const label = 'FY' + q.fy + ' Q' + q.quarter;
    input.onProgress?.({
      percent: Math.round((index / quarters.length) * 100),
      message: 'Projecting ' + label + '...',
    });
    const yearOffset = q.fy - startFY;
    const projectedMissions = applyGrowthModel(
      missions,
      yearOffset,
      growthRate,
      startFY,
      isToggleEnabled('scurve_growth'),
    );

    const sampleStart = q.startMs;
    const sampleEnd = sampleStart + SAMPLE_DAYS * DAY_MS;
    const contactWindows = await computeAllContactWindows({
      missions: projectedMissions,
      antennas,
      startMs: sampleStart,
      endMs: sampleEnd,
      computeContactWindows: input.computeContactWindows,
      random,
    });

    const sorted = projectedMissions.slice().sort((a, b) => a.priorityTier - b.priorityTier);
    const result = quickAnalysis(sorted, antennas, contactWindows, sampleStart, sampleEnd);

    yearlyResults.push({
      fy: q.fy,
      quarter: q.quarter,
      label,
      sla: result.avgSLA,
      conflicts: result.conflicts,
      utilization: result.utilization,
      missionCount: projectedMissions.length,
      startMs: q.startMs,
      endMs: q.endMs,
    });
  }

  const saturationPoint = findSaturationPoint(yearlyResults);
  const recommendations = buildBudgetRecommendations(yearlyResults, saturationPoint, antennas);
  input.onProgress?.({ percent: 100, message: 'Projection complete' });

  return {
    yearlyResults,
    saturationPoint,
    recommendations,
    startFY,
    endFY,
    growthModel,
    generatedAtMs: nowMs(),
  };
}

/**
 * Greedy first-fit in priority order with no preemption: a window overlapping
 * an already scheduled entry on that antenna counts as a conflict, otherwise
 * it is scheduled. SLA only for missions with a minutesPerYear requirement.
 */
export function quickAnalysis(
  sortedMissions: Mission[],
  antennas: Antenna[],
  contactWindows: ContactWindowMap,
  startMs: number,
  endMs: number,
): QuickAnalysisResult {
  const antennaSchedule: Record<string, { startMs: number; endMs: number; missionId: string }[]> = {};
  let conflicts = 0;
  let totalSLA = 0;
  let slaCount = 0;

  for (const a of antennas) antennaSchedule[a.id] = [];

  const hoursInRange = (endMs - startMs) / HOUR_MS;
  const daysInRange = hoursInRange / 24;

  for (const mission of sortedMissions) {
    const missionWindows = contactWindows[mission.id];
    if (!missionWindows) continue;

    let allocatedMin = 0;

    for (const antenna of antennas) {
      const windows = missionWindows[antenna.id];
      if (!windows) continue;

      for (const w of windows) {
        let hasOverlap = false;
        for (const entry of antennaSchedule[antenna.id]) {
          if (w.startMs < entry.endMs && w.endMs > entry.startMs) {
            hasOverlap = true;
            conflicts++;
            break;
          }
        }
        if (!hasOverlap) {
          antennaSchedule[antenna.id].push({ startMs: w.startMs, endMs: w.endMs, missionId: mission.id });
          allocatedMin += w.durationMin;
        }
      }
    }

    const requiredMin = (mission.requirements.minutesPerYear || 0) * (daysInRange / 365);
    if (requiredMin > 0) {
      totalSLA += Math.min(100, Math.round((allocatedMin / requiredMin) * 100));
      slaCount++;
    }
  }

  const utilization: Record<string, number> = {};
  for (const antenna of antennas) {
    let scheduled = 0;
    for (const e of antennaSchedule[antenna.id]) scheduled += (e.endMs - e.startMs) / MINUTE_MS;
    const maxHrs = antenna.capabilities.maxHoursPerDay || 18;
    const maintHrs = antenna.capabilities.maintenanceWindowHrs || 0;
    const netHrs = Math.max(0, maxHrs - maintHrs);
    const maxMin = hoursInRange * 60 * (netHrs / 24);
    utilization[antenna.id] = maxMin > 0 ? Math.min(100, Math.round((scheduled / maxMin) * 100)) : 0;
  }

  return {
    avgSLA: slaCount > 0 ? Math.round(totalSLA / slaCount) : 0,
    conflicts,
    utilization,
  };
}

/**
 * Missions as they stand `yearOffset` years after `currentFY`: unchanged for
 * offset 0 or rate 0. With useSCurve, decommissioned missions whose
 * decommissionFY has arrived are removed and missions with an
 * annualGrowthRate follow a logistic curve centred on peakYear (default 3);
 * everything else compounds dataPerDayGB by (1 + rate) and minutesPerYear by
 * (1 + rate / 2) per year. Returns deep clones.
 */
export function applyGrowthModel(
  missions: Mission[],
  yearOffset: number,
  growthRate: number,
  currentFY: number,
  useSCurve: boolean,
): Mission[] {
  if (yearOffset === 0 || growthRate === 0) return missions;

  const projected: Mission[] = [];
  for (const m of missions) {
    const clone = JSON.parse(JSON.stringify(m)) as Mission;
    const growth: ProjectedGrowth = m.projectedGrowth || {};

    if (useSCurve && growth.lifecycle === 'decommission' && growth.decommissionFY) {
      if (currentFY + yearOffset >= growth.decommissionFY) continue;
    }

    if (useSCurve && growth.annualGrowthRate) {
      const peakYear = growth.peakYear || 3;
      const factor = 1 + growth.annualGrowthRate * sCurve(yearOffset, peakYear);
      if (clone.requirements.dataPerDayGB) {
        clone.requirements.dataPerDayGB *= factor;
      }
      if (clone.requirements.minutesPerYear) {
        clone.requirements.minutesPerYear = Math.round(clone.requirements.minutesPerYear * factor);
      }
    } else {
      if (clone.requirements.dataPerDayGB) {
        clone.requirements.dataPerDayGB *= Math.pow(1 + growthRate, yearOffset);
      }
      if (clone.requirements.minutesPerYear) {
        clone.requirements.minutesPerYear = Math.round(
          clone.requirements.minutesPerYear * Math.pow(1 + growthRate * 0.5, yearOffset),
        );
      }
    }

    projected.push(clone);
  }
  return projected;
}

/** Logistic curve centred on `peak`. */
export function sCurve(year: number, peak: number): number {
  return 1 / (1 + Math.exp(-(year - peak)));
}

/** Federal fiscal quarters (UTC): Q1 Oct to Jan of FY-1, Q2 Jan, Q3 Apr, Q4 Jul. */
export function buildQuarterlyTimeline(startFY: number, endFY: number): Quarter[] {
  const quarters: Quarter[] = [];
  for (let fy = startFY; fy <= endFY; fy++) {
    for (let q = 1; q <= 4; q++) {
      let month: number;
      let year: number;
      if (q === 1) {
        month = 9;
        year = fy - 1;
      } else if (q === 2) {
        month = 0;
        year = fy;
      } else if (q === 3) {
        month = 3;
        year = fy;
      } else {
        month = 6;
        year = fy;
      }

      const startMs = Date.UTC(year, month, 1);
      let endMonth = month + 3;
      let endYear = year;
      if (endMonth > 11) {
        endMonth -= 12;
        endYear++;
      }
      quarters.push({ fy, quarter: q, startMs, endMs: Date.UTC(endYear, endMonth, 1) });
    }
  }
  return quarters;
}

/** The first row where any antenna exceeds 95% utilisation, naming that antenna. */
export function findSaturationPoint(rows: BudgetRow[]): SaturationPoint | null {
  for (const row of rows) {
    let maxUtil = 0;
    let antennaId: string | null = null;
    for (const id of Object.keys(row.utilization || {})) {
      const u = row.utilization[id];
      if (u > maxUtil) {
        maxUtil = u;
        antennaId = id;
      }
    }
    if (maxUtil > 95 && antennaId !== null) {
      return { label: row.label, fy: row.fy, quarter: row.quarter, maxUtil, antennaId };
    }
  }
  return null;
}

/** Recommendation strings for the projection (section 11 wording, commas for the original's dashes). */
export function buildBudgetRecommendations(
  results: BudgetRow[],
  saturationPoint: SaturationPoint | null,
  antennas: Antenna[],
): string[] {
  const recs: string[] = [];
  const shortName = (id: string) => antennas.find((a) => a.id === id)?.shortName ?? id;

  if (saturationPoint) {
    recs.push(
      'By ' +
        saturationPoint.label +
        ', ' +
        shortName(saturationPoint.antennaId) +
        ' reaches ' +
        saturationPoint.maxUtil +
        '% utilization, capacity expansion needed.',
    );
  }

  if (results.length >= 4) {
    const firstSLA = results[0].sla;
    const lastSLA = results[results.length - 1].sla;
    if (lastSLA < firstSLA - 10) {
      recs.push(
        'Overall SLA declines from ' +
          firstSLA +
          '% to ' +
          lastSLA +
          '% over the projection window, additional ground assets recommended.',
      );
    }
  }

  if (results.length >= 4) {
    const firstConflicts = results[0].conflicts;
    const lastConflicts = results[results.length - 1].conflicts;
    if (lastConflicts > firstConflicts * 1.5) {
      recs.push(
        'Scheduling conflicts increase from ' +
          firstConflicts +
          ' to ' +
          lastConflicts +
          ', consider adding alternate antennas or relay capacity.',
      );
    }
  }

  if (results.length > 0) {
    const lastResult = results[results.length - 1];
    const highUtil: string[] = [];
    for (const id of Object.keys(lastResult.utilization || {})) {
      if (lastResult.utilization[id] > 85) highUtil.push(shortName(id));
    }
    if (highUtil.length > 0) {
      recs.push('High utilization antennas by end of projection: ' + highUtil.join(', '));
    }
  }

  if (recs.length === 0) {
    recs.push('Network capacity appears sufficient through the projection window.');
  }

  return recs;
}
